import { useEffect } from "preact/hooks";
import { AuthProvider, useAuth } from "./lib/auth";
import { matchPath, navigate, useRoute, type RouteComponent } from "./lib/router";
import { isConfigured } from "./lib/supabase";

import { Inbox } from "./routes/Inbox";
import { Archive } from "./routes/Archive";
import { Highlights } from "./routes/Highlights";
import { Tags } from "./routes/Tags";
import { Settings as SettingsRoute } from "./routes/Settings";
import { Reader } from "./reader/Reader";
import { SignIn } from "./routes/SignIn";

interface NavItem {
  path: string;
  label: string;
  icon: string;
  match: (p: string) => boolean;
}

const NAV: NavItem[] = [
  { path: "/", label: "Inbox", icon: "📥", match: (p) => p === "/" || p === "/inbox" },
  { path: "/archive", label: "Archive", icon: "🗄️", match: (p) => p === "/archive" },
  { path: "/highlights", label: "Highlights", icon: "🖍️", match: (p) => p === "/highlights" },
  { path: "/tags", label: "Tags", icon: "🏷️", match: (p) => p === "/tags" },
  { path: "/settings", label: "Settings", icon: "⚙️", match: (p) => p === "/settings" },
];

function Nav({ current }: { current: string }) {
  return (
    <>
      <nav class="sidebar" aria-label="Primary">
        <div class="brand">Stash</div>
        {NAV.map((item) => (
          <a
            key={item.path}
            href={`#${item.path}`}
            class={item.match(current) ? "active" : ""}
            aria-current={item.match(current) ? "page" : undefined}
          >
            <span aria-hidden="true">{item.icon}</span>
            <span>{item.label}</span>
          </a>
        ))}
        <div class="spacer" />
        <button class="ghost" onClick={() => navigate("/capture")} aria-label="Add URL">+ Add URL</button>
      </nav>
      <nav class="bottom-nav" aria-label="Primary">
        {NAV.map((item) => (
          <a
            key={item.path}
            href={`#${item.path}`}
            class={item.match(current) ? "active" : ""}
            aria-current={item.match(current) ? "page" : undefined}
          >
            <span aria-hidden="true">{item.icon}</span>
            <span>{item.label}</span>
          </a>
        ))}
      </nav>
    </>
  );
}

interface RouteEntry {
  pattern: string;
  component: RouteComponent;
  auth: boolean;
}

const ROUTES: RouteEntry[] = [
  { pattern: "/", component: Inbox, auth: true },
  { pattern: "/inbox", component: Inbox, auth: true },
  { pattern: "/capture", component: Inbox, auth: true }, // capture modal is part of inbox
  { pattern: "/archive", component: Archive, auth: true },
  { pattern: "/highlights", component: Highlights, auth: true },
  { pattern: "/tags", component: Tags, auth: true },
  { pattern: "/tags/:name", component: Tags, auth: true },
  { pattern: "/settings", component: SettingsRoute, auth: true },
  { pattern: "/articles/:id", component: Reader, auth: true },
];

function Router() {
  const route = useRoute();

  // Reader params
  const readerParams = matchPath("/articles/:id", route.path);

  for (const entry of ROUTES) {
    const params = matchPath(entry.pattern, route.path);
    if (params !== null) {
      const C = entry.component;
      return (
        <div class="app-shell">
          <Nav current={route.path} />
          <main class="app-main" data-route={route.path}>
            <C params={params} />
          </main>
        </div>
      );
    }
  }
  // Reader fallback: if it looks like /articles/:id but matched nothing, still render reader
  if (readerParams) {
    return (
      <div class="app-shell">
        <Nav current="/" />
        <main class="app-main">
          <Reader params={readerParams} />
        </main>
      </div>
    );
  }
  return (
    <div class="app-shell">
      <Nav current="/" />
      <main class="app-main">
        <div class="empty">
          <h2>Not found</h2>
          <p><a href="#/">Back to inbox</a></p>
        </div>
      </main>
    </div>
  );
}

function Shell() {
  const auth = useAuth();

  // Apply theme on first paint to avoid flash
  useEffect(() => {
    // settings effect runs on import; nothing extra needed here
  }, []);

  if (!isConfigured) {
    return (
      <div class="app-main narrow" style="max-width: 32rem; margin: 2rem auto;">
        <div class="banner error">
          Supabase is not configured. Copy <code>.env.example</code> to <code>.env</code> and fill in
          <code> VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code>, then restart <code>pnpm dev</code>.
        </div>
      </div>
    );
  }

  if (auth.loading) {
    return (
      <div class="empty">
        <div class="spinner" />
      </div>
    );
  }

  if (!auth.user) return <SignIn />;
  return <Router />;
}

export function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
